import { useState, useEffect } from 'react'
import {
  Loader2,
  Send,
  Inbox,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  FileText,
  Calendar,
  Users,
  Scale,
  Trophy,
  Archive,
  HelpCircle,
  StickyNote,
  DollarSign,
  Banknote,
  Gavel,
  Shield,
} from 'lucide-react'
import { legalAPI } from '@/utils/api'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface TimelineEvent {
  id: string
  event_type: 'action' | 'correspondence'
  title: string
  description: string
  icon: string
  date: string
  visible_to_client: boolean
  party_type: string | null
  direction: string | null
}

interface ClientTimelineProps {
  caseId: number
}

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'send': Send,
  'inbox': Inbox,
  'mail': Mail,
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'alert-circle': AlertCircle,
  'clock': Clock,
  'file-text': FileText,
  'calendar': Calendar,
  'users': Users,
  'scale': Scale,
  'trophy': Trophy,
  'archive': Archive,
  'help-circle': HelpCircle,
  'sticky-note': StickyNote,
  'dollar-sign': DollarSign,
  'banknote': Banknote,
  'gavel': Gavel,
  'shield': Shield,
  'check': CheckCircle,
  'file-question': HelpCircle,
  'file-check': FileText,
  'handshake': Users,
  'circle': AlertCircle,
}

function getIcon(iconName: string) {
  return ICON_MAP[iconName] || AlertCircle
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      return `vor ${diffMinutes} Min.`
    }
    return `vor ${diffHours} Std.`
  }
  if (diffDays === 1) return 'gestern'
  if (diffDays < 7) return `vor ${diffDays} Tagen`
  if (diffDays < 30) return `vor ${Math.floor(diffDays / 7)} Wochen`
  return formatDate(dateString)
}

function getEventColor(event: TimelineEvent): string {
  if (event.event_type === 'correspondence') {
    if (event.direction === 'outbound') return 'bg-blue-500'
    return 'bg-green-500'
  }

  // Default colors based on party type
  if (event.party_type === 'rsv') return 'bg-amber-500'
  if (event.party_type === 'court') return 'bg-purple-500'
  if (event.party_type === 'defendant') return 'bg-slate-500'

  return 'bg-blue-500'
}

export function ClientTimeline({ caseId }: ClientTimelineProps) {
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchTimeline = async () => {
      setIsLoading(true)
      try {
        const response = await legalAPI.getTimeline(caseId)
        // Filter to only show client-visible events
        const visibleEvents = (response.timeline || []).filter(
          (event: TimelineEvent) => event.visible_to_client
        )
        setTimeline(visibleEvents)
      } catch (error) {
        console.log('No timeline data found')
        setTimeline([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchTimeline()
  }, [caseId])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
      </div>
    )
  }

  if (timeline.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-700 mb-2">Noch keine Aktivitäten</h3>
        <p className="text-slate-500 text-sm">
          Sobald es Neuigkeiten zu Ihrem Fall gibt, werden sie hier angezeigt.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
        <FileText className="h-5 w-5 text-blue-500" />
        Fallverlauf
      </h3>

      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />

        <div className="space-y-4">
          {timeline.map((event) => {
            const IconComponent = getIcon(event.icon)
            const eventColor = getEventColor(event)

            return (
              <div key={event.id} className="relative flex items-start gap-4 pl-2">
                {/* Icon */}
                <div
                  className={cn(
                    'relative z-10 w-8 h-8 rounded-full flex items-center justify-center',
                    eventColor
                  )}
                >
                  <IconComponent className="h-4 w-4 text-white" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-slate-900">{event.title}</h4>
                        {event.party_type && (
                          <Badge variant="outline" className="text-xs">
                            {event.party_type === 'rsv' && 'RSV'}
                            {event.party_type === 'court' && 'Gericht'}
                            {event.party_type === 'defendant' && 'Fluggesellschaft'}
                            {event.party_type === 'client' && 'Sie'}
                            {event.party_type === 'other' && 'Sonstige'}
                          </Badge>
                        )}
                      </div>

                      {event.description && (
                        <p className="text-sm text-slate-500 mt-1">{event.description}</p>
                      )}
                    </div>

                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      {formatRelativeTime(event.date)}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
